import { useContext, useEffect, useState } from "react";
import { ApiClientContext } from "../api/ApiClientContext";
import { useAuth } from "../auth/AuthContext";
import { useOnline } from "../offline/useOnline";

// Whether this team has sub-clients, asked once per team per page load: the
// header mounts on every route change and must not hit /api/teams each time.
// Successes only — a probe that failed because the tab was offline or the
// Worker was paused drops out again, or a parent loses the Usage link until
// they reload the whole app.
const memo = new Map<string, Promise<boolean>>();

export function resetCanSeeUsageMemo(): void {
  memo.clear();
}

/** Spec §9: the Usage link shows for admin, or for a parent (the same
 *  /api/teams probe the Flights page uses), and never offline. Reads the
 *  client straight from context so a header rendered without one (tests,
 *  the login page) simply says no. */
export function useCanSeeUsage(): boolean {
  const client = useContext(ApiClientContext);
  const auth = useAuth();
  const online = useOnline();
  const slug = auth.team?.slug ?? null;
  const admin = auth.team?.role === "admin";
  const [parent, setParent] = useState(false);

  useEffect(() => {
    if (admin || slug === null || client === null || !online) return undefined;
    let cancelled = false;
    let probe = memo.get(slug);
    if (probe === undefined) {
      // Start on a resolved promise so a client that throws on the spot (a
      // partial stand-in, a stubbed-out method) fails the same way a rejected
      // fetch does: no link, rather than a header that crashes the page. The
      // warning is the only trace such a client leaves, so keep it.
      probe = Promise.resolve()
        .then(() => client.listTeams())
        .then((r) => r.teams.length > 0)
        .catch((err: unknown) => {
          console.warn("useCanSeeUsage: listTeams probe failed", err);
          memo.delete(slug);
          return false;
        });
      memo.set(slug, probe);
    }
    void probe.then((v) => {
      if (!cancelled) setParent(v);
    });
    return () => {
      cancelled = true;
    };
  }, [admin, slug, client, online]);

  if (client === null || !online) return false;
  return admin || parent;
}
